import { Box } from '@mui/material'
import { formatApiDate } from '~/shared/lib/date'
import FormAnswerDisplay from './FormAnswerDisplay'
import { getFormSubmissionStatus } from './utils'
import { FormSubmissionRetrieveResponse } from '@fireflyhealth/api'
import { PatientUser } from '~/shared/types'
import { camelize, camelizeKeys } from 'humps'

const UNANSWERED_SX = { color: 'grey.500', whiteSpace: 'pre-line' }

const ANSWERED_SX = { color: 'common.black', whiteSpace: 'pre-line' }

const NUMBERED_SX = {
  '&::before': { counterIncrement: 'section', content: 'counters(section, "") ". "' },
}

/**
 * What `<FormAnswerDisplay>` accepts, which is what the submission data holds. An absent key is
 * a question the patient was never shown, which that component renders differently from an
 * empty answer — so the `undefined` in this union is load-bearing.
 */
type Answer = React.ComponentProps<typeof FormAnswerDisplay>['answer']

const FormSubmissionOverview: React.FC<{
  submission: FormSubmissionRetrieveResponse
  user: PatientUser
}> = props => {
  const { submission } = props

  return (
    <Box>
      <h2>{submission.form.title}</h2>
      <p>
        {props.user.first_name} {props.user.last_name} -{' '}
        {getFormSubmissionStatus({
          completedAt: submission.completed_at,
          expiredAt: submission.expired_at,
          startedAt: submission.started_at,
        })}
      </p>
      {submission.completed_at ? (
        <p>Completed on {formatApiDate(submission.completed_at, 'MMMM d, yyyy h:mm a') ?? ''}</p>
      ) : null}
      {submission.form.sections.map(section => (
        <Box key={section.id} sx={{ mb: 2 }}>
          <Box component="h3" sx={{ counterReset: 'section 0' }}>
            {section.title}
          </Box>
          {section.questions.map(question => {
            // Form submission data properties, which must match question UIDs,
            // were historically camelized by the legacy API client, and we
            // compensated by similarly camelizing question UIDs.
            //
            // While we could choose to simply decamelize the question UIDs, the
            // `decamelize` method from `humps` cannot reliably undo
            // camelization done by the backend. For example, `phq9` does not
            // decamelize to `phq_9`.
            //
            // Therefore, we camelize both the question UIDs and submission data
            // keys to match.
            const uid = camelize(question.uid)
            const submissionData = submission.data
              ? (camelizeKeys(submission.data) as Record<string, Answer>)[uid]
              : undefined
            const isUnanswered = submissionData === undefined
            const isNumbered = question.type !== 'info'
            return (
              <Box key={`question_${question.id}`} sx={{ mb: 1 }}>
                <p>
                  <Box
                    component="b"
                    sx={[
                      isUnanswered && UNANSWERED_SX,
                      !isUnanswered && isNumbered && ANSWERED_SX,
                      isNumbered && NUMBERED_SX,
                    ]}
                  >
                    {question.text}
                  </Box>
                </p>
                <FormAnswerDisplay question={question} answer={submissionData} />
              </Box>
            )
          })}
        </Box>
      ))}
    </Box>
  )
}

export default FormSubmissionOverview
